// ============================================================
// DecoupePlan — entourer chaque pièce d'un plan qui en porte plusieurs.
// ------------------------------------------------------------
// Philippe, 13/10 : « 2 pièces sur le plan… est-il possible de faire
// quelque chose ? » Le deviseur trace un rectangle par pièce, et au plus
// un rectangle « cartouche » ; chaque pièce devient une page à part
// entière, lue comme une page déposée (docs/OUTIL-DECOUPE-PLANS.md).
//
// Le comportement est celui de l'éditeur d'AGC ; le code non : les
// rectangles sont dessinés en SVG avec des ATTRIBUTS (aucun `style` dans
// `features/`, §8), leurs couleurs sont des jetons, et l'image est celle
// que le serveur rend — la même que le visualiseur, `/Rotate` appliqué.
//
// L'éditeur est HORS de l'enregistrement automatique : découper est une
// action serveur explicite. On enregistre le devis avant d'ouvrir
// (`avant`), on recharge après (`apres`).
// ============================================================

import { useEffect, useRef, useState } from 'react';
import Modal from '../../ui/Modal.jsx';
import Button from '../../ui/Button.jsx';
import { TextField } from '../../ui/TextField.jsx';
import { ErrorState, Skeleton } from '../../ui/States.jsx';
import api, { fetchBlob } from '../../lib/api.js';
import { problemesDecoupe } from '@shared/decoupe.js';
import {
  zoneDepuisGlisser, ajouterZone, zonesDepuisServeur, rangCouleur,
} from './decoupeZones.js';
import DecoupeConfirmation from './DecoupeConfirmation.jsx';

const ECHELLE = 1000; // le viewBox du SVG : les zones normalisées × 1000

export function DecoupePlan({ devisId, pageId, avant, apres, onClose }) {
  const [prep, setPrep] = useState(null);
  const [image, setImage] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [zones, setZones] = useState([]);
  const [type, setType] = useState('piece');
  const [trace, setTrace] = useState(null);
  const [etape, setEtape] = useState('trace');
  const svg = useRef(null);
  // `avant` change à chaque rendu du parent : on ne le met pas dans les
  // dépendances, sinon chaque rendu rechargerait la page à découper.
  const avantRef = useRef(avant);

  useEffect(() => {
    let vivant = true;
    let url = null;
    (async () => {
      try {
        await avantRef.current?.();
        const p = await api.get(`/devis/${devisId}/plans/${pageId}/decoupe`);
        const blob = await fetchBlob(`/devis/${devisId}/plans/fichier/${p.page.id}/image`);
        if (!vivant) return;
        url = URL.createObjectURL(blob);
        setPrep(p);
        setZones(zonesDepuisServeur(p.zones));
        setImage(url);
      } catch (err) {
        if (vivant) setErreur(err);
      }
    })();
    return () => {
      vivant = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [devisId, pageId]);

  /** La position du pointeur, en pixels dans le cadre de l'image. */
  const point = (e) => {
    const r = svg.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, largeur: r.width, hauteur: r.height };
  };
  const debut = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = point(e);
    setTrace({ a: p, b: p });
  };
  const bouge = (e) => {
    if (trace) setTrace({ ...trace, b: point(e) });
  };
  const fin = (e) => {
    if (!trace) return;
    const b = point(e);
    const zone = zoneDepuisGlisser(trace.a, b, b.largeur, b.hauteur);
    setTrace(null);
    if (zone) setZones((zs) => ajouterZone(zs, zone, type));
  };
  const enCours = trace ? zoneDepuisGlisser(trace.a, trace.b, trace.b.largeur, trace.b.hauteur) : null;

  const renommer = (i, libelle) => setZones((zs) => zs.map((z, j) => (j === i ? { ...z, libelle } : z)));
  const retirer = (i) => setZones((zs) => zs.filter((_, j) => j !== i));

  const problemes = problemesDecoupe(zones);
  const nbPieces = zones.filter((z) => z.type === 'piece').length;
  const verrouille = prep?.verrouille;

  const terminer = () => {
    onClose();
    apres?.();
  };

  return (
    <Modal open large onClose={onClose}
      title={prep ? `Découper la page ${prep.page.page_number ?? ''} — une zone par pièce` : 'Découper le plan'}>
      {erreur ? <ErrorState error={erreur} title="Découpe indisponible" /> : null}
      {!erreur && !prep ? <Skeleton height="60vh" /> : null}
      {prep && etape !== 'trace' ? (
        <DecoupeConfirmation
          devisId={devisId} prep={prep} zones={zones} mode={etape}
          onRetour={() => setEtape('trace')} onTermine={terminer}
        />
      ) : null}
      {prep && etape === 'trace' ? (
        <div className="decoupe">
          <div className="decoupe__plan">
            <fieldset className="decoupe__type" disabled={verrouille}>
              <legend className="field__label">Ce rectangle est :</legend>
              <label className="row">
                <input type="radio" name="type-zone" aria-label="Une pièce" checked={type === 'piece'}
                  onChange={() => setType('piece')} />
                <span>une pièce</span>
              </label>
              <label className="row">
                <input type="radio" name="type-zone" aria-label="Le cartouche commun" checked={type === 'cartouche'}
                  onChange={() => setType('cartouche')} />
                <span>le cartouche commun (un seul par page)</span>
              </label>
            </fieldset>
            <div className="decoupe__cadre">
              <img className="decoupe__image" src={image} alt="La page à découper" draggable={false} />
              {/* Tracer au glisser : la souris, le doigt ou le stylet. Le
                  clavier n'a pas d'équivalent — la découpe est un geste
                  visuel par nature, comme l'était celle d'AGC. */}
              <svg ref={svg} className="decoupe__calque" viewBox={`0 0 ${ECHELLE} ${ECHELLE}`}
                preserveAspectRatio="none" aria-hidden="true"
                onPointerDown={verrouille ? undefined : debut} onPointerMove={bouge} onPointerUp={fin}>
                {zones.map((z, i) => (
                  <rect key={`${z.type}-${i}`} x={z.x * ECHELLE} y={z.y * ECHELLE}
                    width={z.w * ECHELLE} height={z.h * ECHELLE}
                    className={z.type === 'cartouche'
                      ? 'decoupe__zone decoupe__zone--cartouche'
                      : `decoupe__zone decoupe__zone--${rangCouleur(zones, i)}`} />
                ))}
                {enCours ? (
                  <rect x={enCours.x * ECHELLE} y={enCours.y * ECHELLE} width={enCours.w * ECHELLE}
                    height={enCours.h * ECHELLE} className="decoupe__zone decoupe__zone--trace" />
                ) : null}
              </svg>
            </div>
          </div>

          <aside className="decoupe__liste">
            {verrouille ? (
              <p className="field__error">Ce devis est verrouillé : créez une révision pour découper.</p>
            ) : null}
            {zones.length === 0 ? (
              <p className="field__hint">Glissez sur le plan pour entourer la première pièce.</p>
            ) : null}
            <ul className="decoupe__zones">
              {zones.map((z, i) => (
                <li key={`${z.type}-${i}`} className="decoupe__ligne">
                  <span aria-hidden="true" className={z.type === 'cartouche'
                    ? 'decoupe__puce decoupe__puce--cartouche'
                    : `decoupe__puce decoupe__puce--${rangCouleur(zones, i)}`} />
                  {z.type === 'cartouche' ? (
                    <span className="decoupe__nom">Cartouche commun</span>
                  ) : (
                    <TextField label={`Nom de la zone ${i + 1}`} hideLabel value={z.libelle ?? ''}
                      maxLength={80} disabled={verrouille} onChange={(e) => renommer(i, e.target.value)} />
                  )}
                  <Button size="sm" variant="ghost" disabled={verrouille} onClick={() => retirer(i)}
                    aria-label={`Supprimer ${z.libelle ?? 'le cartouche'}`}>
                    Supprimer
                  </Button>
                </li>
              ))}
            </ul>
            {problemes.length && zones.length ? (
              <ul className="field__hint">{problemes.map((p) => <li key={p}>{p}</li>)}</ul>
            ) : null}
            <div className="decoupe__actions">
              <Button variant="primary" disabled={verrouille || problemes.length > 0}
                onClick={() => setEtape('decoupe')}>
                {`Créer ${nbPieces} pièce${nbPieces > 1 ? 's' : ''}`}
              </Button>
              {prep.decoupee ? (
                <Button variant="ghost" disabled={verrouille} onClick={() => setEtape('annulation')}>
                  Annuler la découpe
                </Button>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}
    </Modal>
  );
}

export default DecoupePlan;
